import React, { useState } from 'react'
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import {
  useGetStoreStocksQuery,
  useDeleteStoreStockMutation
} from '../../slices/storeStocksApiSlice'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

const StoreStockListScreen = () => {
  // ─── State ────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState('')
  const [refineFilter, setRefineFilter] = useState('') // second filter
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // ─── Data Fetching ─────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    error,
    refetch
  } = useGetStoreStocksQuery(pageNumber)
  const [deleteStoreStock, { isLoading: loadingDelete }] =
    useDeleteStoreStockMutation()

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this storeStock?')) return
    try {
      await deleteStoreStock(id).unwrap()
      toast.success('StoreStock deleted!')
      refetch()
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete')
    }
  }

  const paginationHandler = (page) => setPageNumber(page)

  // ─── Filtering Logic (ORIGINAL) ────────────────────────────────────────────
  const filterStoreStocks = () => {
    if (!data?.storeStocks) return []
    return data.storeStocks.filter((storeStock) => {
      const storeStockDate = new Date(storeStock.date)
      const inRange =
        (!startDate || storeStockDate >= new Date(startDate)) &&
        (!endDate || storeStockDate <= new Date(endDate))

      const noteMatch = storeStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemMatch = storeStock.storeStockItems.some((i) =>
        i.name.toLowerCase().includes(filter.toLowerCase())
      )

      return inRange && (noteMatch || itemMatch)
    })
  }

  // ─── Total Value (refined by second filter) ────────────────────────────────
  const calculateTotalStoreStocks = () => {
    return filterStoreStocks().reduce((acc, storeStock) => {
      const noteMatch = storeStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? storeStock.storeStockItems
        : storeStock.storeStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return acc + itemsAfterSecond.reduce((sum, i) => sum + i.qty * i.price, 0)
    }, 0)
  }

  const calculateTotalQtys = () => {
    return filterStoreStocks().reduce((acc, storeStock) => {
      const noteMatch = storeStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? storeStock.storeStockItems
        : storeStock.storeStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return acc + itemsAfterSecond.reduce((sum, i) => sum + i.qty, 0)
    }, 0)
  }

  // ─── Excel Export (refined by second filter) ───────────────────────────────
  const exportToExcel = () => {
    const rows = filterStoreStocks().flatMap((storeStock) => {
      const noteMatch = storeStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? storeStock.storeStockItems
        : storeStock.storeStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return itemsAfterSecond.map((i) => ({
        Date: new Date(storeStock.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: storeStock.note || 'N/A'
      }))
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'StoreStocks')
    XLSX.writeFile(wb, 'StoreStocks_Report.xlsx')
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>StoreStocks</h1>
          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>

          <Link to='/StoreAdmin/storeStocksearchReplace' className='btn btn-light my-3'>
            Search
          </Link>
          <Link to='/storeAdmin/storeStocknotesearchReplace' className='btn btn-light my-3'>
            NoteSearch
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product name"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Qty: {calculateTotalQtys().toFixed(2)}</h4>
          <h4>Total StoreStocks: ${calculateTotalStoreStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading storeStocks'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterStoreStocks().map((storeStock) => {
                const noteMatch = storeStock.note
                  ?.toLowerCase()
                  .includes(filter.toLowerCase())

                const itemsAfterFirst = noteMatch
                  ? storeStock.storeStockItems
                  : storeStock.storeStockItems.filter((i) =>
                      i.name.toLowerCase().includes(filter.toLowerCase())
                    )

                const itemsAfterSecond = itemsAfterFirst.filter((i) =>
                  i.name.toLowerCase().includes(refineFilter.toLowerCase())
                )

                const sorted = [...itemsAfterSecond].sort((a, b) =>
                  a.name.localeCompare(b.name)
                )

                return sorted.map((item) => (
                  <tr key={`${storeStock._id}-${item.name}`}>
                    <td>{new Date(storeStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>{storeStock.note || 'N/A'}</td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="thumb"
                          className="thumbnail-img me-2"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px'
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/storeStock/${storeStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        as={Link}
                        to={`/storeStock/${storeStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(storeStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StoreStockListScreen
